import { Component, computed, inject, output, signal } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { KcInputComponent, KcButtonComponent, KcIconComponent } from '@kaspacom/ui-kit';
import { NewWalletFlowService } from '../../service/new-wallet-flow.service';

@Component({
  selector: 'app-create-password-new-wallet-step',
  imports: [
    ReactiveFormsModule,
    KcInputComponent,
    KcButtonComponent,
    KcIconComponent,
  ],
  templateUrl: './create-password-new-wallet-step.component.html',
  styleUrl: './create-password-new-wallet-step.component.scss',
})
export class CreatePasswordNewWalletStepComponent {
  next = output<void>();
  previous = output<void>();

  private readonly newWalletFlowService = inject(NewWalletFlowService);

  private readonly fb = inject(FormBuilder);

  passwordForm = this.fb.group(
    {
      password: [
        this.newWalletFlowService.newWallet().password,
        [
          Validators.required,
          Validators.minLength(8),
          Validators.pattern(/^(?=.*[A-Za-z])(?=.*\d)(?=.*[^A-Za-z0-9]).+$/),
        ],
      ],
      confirmPassword: [
        this.newWalletFlowService.newWallet().confirmPassword,
        [Validators.required],
      ],
    },
    { validators: this.passwordMatchValidator },
  );

  passwordMatchValidator(control: AbstractControl): ValidationErrors | null {
    const password = control.get('password');
    const confirmPassword = control.get('confirmPassword');

    if (
      password &&
      confirmPassword &&
      password.value !== confirmPassword.value
    ) {
      return { passwordsDoNotMatch: true };
    }
    return null;
  }

  passwordType = signal<'password' | 'text'>('password');
  passwordConfirmType = signal<'password' | 'text'>('password');
  passwordIcon = computed(() =>
    this.passwordType() === 'password' ? 'icon-eye' : 'icon-eye-crossed',
  );
  passwordConfirmIcon = computed(() =>
    this.passwordConfirmType() === 'password' ? 'icon-eye' : 'icon-eye-crossed',
  );

  togglePwVisibility(): void {
    this.passwordType.set(
      'password' === this.passwordType() ? 'text' : 'password',
    );
  }
  toggleConfirmPwVisibility(): void {
    this.passwordConfirmType.set(
      'password' === this.passwordConfirmType() ? 'text' : 'password',
    );
  }

  isInvalid(controlName: string): boolean {
    const control = this.passwordForm.get(controlName);
    const passwordsMatchError =
      this.passwordForm.hasError('passwordsDoNotMatch') &&
      (control ? control.dirty || control.touched : false);
    const formCheckResult = control
      ? control.invalid && (control.dirty || control.touched)
      : false;
    return (
      formCheckResult ||
      (controlName === 'confirmPassword' && passwordsMatchError)
    );
  }

  getPasswordErrorMessage(): string | undefined {
    if (this.passwordForm.hasError('required', 'password')) {
      return 'Password is required';
    }
    if (this.passwordForm.hasError('minlength', 'password')) {
      return 'Password is too short';
    }
    if (this.passwordForm.hasError('pattern', 'password')) {
      return 'Password must contain letters, numbers and special characters';
    }
    return undefined;
  }

  getConfirmPasswordErrorMessage(): string | undefined {
    if (this.passwordForm.hasError('passwordsDoNotMatch')) {
      return 'Passwords do not match';
    }
    return undefined;
  }

  onSubmit(): void {
    if (!this.passwordForm.valid) {
      console.log('Form is invalid');
      return;
    }
    const formValue = this.passwordForm.value;
    this.newWalletFlowService.submitPasswordStep(
      formValue.password!,
      formValue.confirmPassword!,
    );
    this.next.emit();
  }
}
